import type { QueryClient } from '@tanstack/react-query';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';

/**
 * Header data the search card already has. `image` is the card's URL verbatim, so the header's
 * first frame is a browser-cache hit. Not seeded: the card's view count (a rounded string such as
 * "2K" — the profile shows "2.7K", and Intl formatting a string gave "NaN") and its rate (the profile
 * may hide rates the card shows); both get a placeholder until their piece arrives.
 */
export type ProfileSeed = {
  display_name: string;
  image?: string;
  expertiseText?: string;
  /** keyword tags shown as extra chips (same list the profile fetches from search) */
  keywordTags?: string[];
  /** card's compact view count, e.g. "15K" */
  view?: string;
  rate?: number;
  ratesCount?: number;
  /** years of experience as the profile shows it (search's `experience` is one year ahead) */
  experience?: number;
  /** Booking hints for smart skeletons: online visit (with rial price) and in-person booking. */
  online?: { price?: number } | false;
  presence?: boolean;
};

const decode = (s: string) => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/** Header data for an instant first frame, taken from a search result the user just tapped. */
export const getProfileSeedFromSearch = (queryClient: QueryClient, slug: string): ProfileSeed | null => {
  const wanted = decode(slug);
  for (const [, data] of queryClient.getQueriesData([ServerStateKeysEnum.Search])) {
    const results = (data as any)?.search?.result;
    if (!Array.isArray(results)) continue;
    const hit = results.find((r: any) => r?.type === 'doctor' && typeof r?.slug === 'string' && decode(r.slug) === wanted);
    if (hit?.display_name) {
      return {
        display_name: hit.display_name,
        image: typeof hit.image === 'string' && hit.image ? hit.image : undefined,
        expertiseText: hit.display_expertise,
        keywordTags: Array.isArray(hit.keyword_tags_user_priority_1) ? hit.keyword_tags_user_priority_1.filter(Boolean) : undefined,
        view: typeof hit.view === 'string' && hit.view ? hit.view : undefined,
        rate: typeof hit.rate_info?.rate === 'number' ? hit.rate_info.rate : undefined,
        ratesCount: typeof hit.rate_info?.rates_count === 'number' ? hit.rate_info.rates_count : undefined,
        online:
          Array.isArray(hit.center_id) && hit.center_id.includes('5532')
            ? { price: hit.consult_services?.find?.((c: any) => c?.free_price != null)?.free_price }
            : false,
        // an active (status 1) office / clinic other than the online-visit center
        presence: Array.isArray(hit.centers) ? hit.centers.some((c: any) => String(c?.id) !== '5532' && c?.status === 1) : undefined,
        experience: typeof hit.experience === 'number' && hit.experience > 1 ? hit.experience - 1 : undefined,
      };
    }
  }
  return null;
};
